import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { RegisterLink } from "../../components/RegisterLink";
import { usePageSeo } from "../../lib/seo";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { getSeoPage, seoPages, type SeoPageConfig } from "./content";

function injectFaqSchema(page: SeoPageConfig) {
  const id = "seo-faq-schema";
  document.getElementById(id)?.remove();
  if (!page.faqs?.length) return;

  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.text = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
  document.head.appendChild(script);
}

export default function SeoPage({ slug }: { slug: string }) {
  const page = getSeoPage(slug);

  usePageSeo({
    title: page?.title ?? "",
    description: page?.description ?? "",
    keywords: page?.keywords,
    path: page ? `/${page.slug}` : "/",
  });

  useEffect(() => {
    if (!page) return;
    injectFaqSchema(page);
    return () => {
      document.getElementById("seo-faq-schema")?.remove();
    };
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const related = seoPages.filter((item) => item.slug !== page.slug);

  return (
    <div className="min-h-screen bg-[#0a0e13] text-white pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-0">
      <Navbar />
      <main className="pt-36 md:pt-40">
        <article className="container mx-auto max-w-4xl px-4 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-gray-400">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="text-yellow-400 hover:text-yellow-300">Kingbet9</Link></li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-gray-300">{page.h1}</li>
            </ol>
          </nav>
          <h1 className="text-3xl md:text-5xl font-black leading-tight" style={{ fontFamily: "Orbitron, sans-serif" }}>
            {page.h1}
          </h1>
          {page.intro.map((paragraph) => (
            <p key={paragraph} className="mt-4 text-base md:text-lg leading-relaxed text-gray-300">
              {paragraph}
            </p>
          ))}

          {page.sections.map((section) => (
            <section key={section.h2} className="mt-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white">{section.h2}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="mt-3 leading-relaxed text-gray-300">
                  {paragraph}
                </p>
              ))}
              {section.subsections?.map((subsection) => (
                <div key={subsection.h3} className="mt-6">
                  <h3 className="text-xl font-semibold text-yellow-300">{subsection.h3}</h3>
                  {subsection.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="mt-3 leading-relaxed text-gray-300">
                      {paragraph}
                    </p>
                  ))}
                  {subsection.points?.map((point) => (
                    <div key={point.h4} className="mt-5 rounded-xl border border-white/10 bg-white/5 p-5">
                      <h4 className="text-lg font-semibold text-white">{point.h4}</h4>
                      {point.paragraphs.map((paragraph) => (
                        <p key={paragraph} className="mt-2 leading-relaxed text-gray-300">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ))}

          {page.faqs && page.faqHeading ? (
            <section className="mt-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white">{page.faqHeading}</h2>
              <div className="mt-4 space-y-5">
                {page.faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-xl font-semibold text-yellow-300">{faq.question}</h3>
                    <p className="mt-2 leading-relaxed text-gray-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <div className="mt-10">
            <RegisterLink
              source={`seo_${page.slug}`}
              label="Register at Kingbet9"
              className="inline-block rounded-md bg-gradient-to-r from-yellow-400 to-yellow-600 px-8 py-3 font-black text-black hover:shadow-[0_0_25px_rgba(255,215,0,0.6)]"
            >
              Register at Kingbet9
            </RegisterLink>
          </div>

          <section className="mt-12">
            <h2 className="text-2xl font-bold">More Kingbet9 Pages</h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {related.map((item) => (
                <li key={item.slug}>
                  <Link to={`/${item.slug}`} className="text-yellow-400 hover:text-yellow-300">
                    {item.h1}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}
